export type ChartPoint = { x: number; y: number };
export type NetworkSample = { observedAt: string; receive: number; transmit: number };

/** Shared zero-based scale; integer counts never get duplicate rounded ticks. */
export function chartScale(maximum: number, integer = false) {
  const safe = Number.isFinite(maximum) && maximum > 0 ? maximum : 1;
  const rough = safe / 4;
  const magnitude = 10 ** Math.floor(Math.log10(rough));
  const multiple = [1, 2, 5, 10].find(value => value * magnitude >= rough) ?? 10;
  const step = Math.max(integer ? 1 : 0, multiple * magnitude);
  const count = Math.ceil(safe / step);
  const ticks = Array.from({ length: count + 1 }, (_, index) => Number((index * step).toPrecision(12)));
  return { max: ticks.at(-1)!, ticks };
}

export function chartTickIndexes(count: number, capacity: number) {
  if (!count) return [];
  if (count === 1) return [0];
  const ticks = Math.min(count, Math.max(2, capacity));
  return Array.from({ length: ticks }, (_, index) => Math.round(index * (count - 1) / (ticks - 1)));
}

/** Shape-preserving cubic interpolation: no manufactured peaks or negative rates. */
export function smoothPath(points: ChartPoint[]) {
  if (!points.length) return '';
  let path = `M ${points[0].x.toFixed(2)} ${points[0].y.toFixed(2)}`;
  const slopes = points.slice(0, -1).map((point, index) => (points[index + 1].y - point.y) / (points[index + 1].x - point.x || 1));
  const tangents = points.map((_point, index) => {
    if (!index) return slopes[0] ?? 0;
    if (index === points.length - 1) return slopes.at(-1) ?? 0;
    const before = slopes[index - 1], after = slopes[index];
    return before * after <= 0 ? 0 : 2 * before * after / (before + after);
  });
  for (let index = 0; index < points.length - 1; index++) {
    const start = points[index], end = points[index + 1], dx = (end.x - start.x) / 3;
    const bounded = (value: number) => Math.min(Math.max(start.y, end.y), Math.max(Math.min(start.y, end.y), value));
    path += ` C ${(start.x + dx).toFixed(2)} ${bounded(start.y + tangents[index] * dx).toFixed(2)} ${(end.x - dx).toFixed(2)} ${bounded(end.y - tangents[index + 1] * dx).toFixed(2)} ${end.x.toFixed(2)} ${end.y.toFixed(2)}`;
  }
  return path;
}

export function networkWindow(history: NetworkSample[]) {
  const byTime = new Map<number, NetworkSample>();
  history.forEach(sample => {
    const time = Date.parse(sample.observedAt);
    if (Number.isFinite(time) && Number.isFinite(sample.receive) && sample.receive >= 0 && Number.isFinite(sample.transmit) && sample.transmit >= 0) byTime.set(time, sample);
  });
  const end = Math.max(0, ...byTime.keys());
  const start = end - 60_000;
  const entries = [...byTime.entries()].filter(([time]) => time >= start).sort(([a], [b]) => a - b).slice(-12);
  return { samples: entries.map(([, sample]) => sample), positions: entries.map(([time]) => (time - start) / 60_000), start, end };
}
